import { FC, useRef } from 'react';

import { MagritteNotificationName } from 'utils/constants/notification';

import { Button, Flex, Link, Text, TooltipHover } from '@hh.ru/magritte-ui';
import { CopyOutlinedSize16 } from '@hh.ru/magritte-ui/icon';

import useTrls from 'hooks/translation/useTrls';

import { DashboardCampaign } from 'store/models/campaignsDashboard';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch } from 'store/utils';

const TrlKeys = {
    vacancyIdCopyIconTooltip: 'CampaignsDashboard.CampaignsTable.CampaignPromotionLink.vacancyIdCopyIconTooltip',
    linkCopyIconTooltip: 'CampaignsDashboard.CampaignsTable.CampaignPromotionLink.linkCopyIconTooltip',
    vacancyNameTooltip: 'CampaignsDashboard.CampaignsTable.CampaignPromotionLink.vacancyNameTooltip',
    vacancyLink: 'CampaignsDashboard.CampaignsTable.CampaignPromotionLink.vacancyLink',
    goToVacancy: 'CampaignsDashboard.CampaignsTable.CampaignPromotionLink.goToVacancy',
    openLink: 'CampaignsDashboard.CampaignsTable.CampaignPromotionLink.openLink',
};

interface CampaignPromotionLinkProps {
    campaign: DashboardCampaign;
    isMobile?: boolean;
}

export const CampaignPromotionLink: FC<CampaignPromotionLinkProps> = ({ campaign, isMobile = false }) => {
    const { vacancyName, vacancyId } = campaign;
    const campaignId = campaign.id;

    const vacancyUrl = `https://hh.ru/vacancy/${vacancyId}`;
    const urlFromBanner = campaign.banners[0].url;
    const hrefLink = vacancyId ? vacancyUrl : urlFromBanner;

    const trls = useTrls();
    const dispatch = useDispatch();
    const activatorRef = useRef<HTMLDivElement>(null);
    const copyIconActivatorRef = useRef<HTMLDivElement>(null);

    const copyToClipboard = async (value: string) => {
        await navigator.clipboard.writeText(value);
        dispatch(
            addMagritteNotification(
                vacancyId
                    ? MagritteNotificationName.VacancyIdCopyToClipboardSuccess
                    : MagritteNotificationName.LinkCopyToClipboardSuccess
            )
        );
    };

    return (
        <>
            <Flex align="center" gap={4}>
                <CopyOutlinedSize16
                    initialColor="secondary"
                    onClick={() => copyToClipboard(vacancyId ? String(vacancyId) : urlFromBanner)}
                    data-qa={`campaign-promotion-link-copy-${campaignId}`}
                    ref={copyIconActivatorRef}
                />
                <Link
                    href={hrefLink}
                    target="_blank"
                    mode="secondary"
                    style="neutral"
                    inline
                    data-qa={`campaign-promotion-link-${campaignId}`}
                >
                    <Text typography="label-3-regular" maxLines={1}>
                        <span ref={activatorRef}>
                            {vacancyId ? trls(TrlKeys.vacancyLink, [vacancyId]) : urlFromBanner}
                        </span>
                    </Text>
                </Link>
            </Flex>
            {!isMobile && (
                <>
                    <TooltipHover
                        delay={500}
                        placement="top-center"
                        activatorRef={activatorRef}
                        buttons={
                            <Button
                                Element="a"
                                href={hrefLink}
                                target="_blank"
                                size="medium"
                                mode="primary"
                                style="constant"
                                stretched
                            >
                                {vacancyId ? trls(TrlKeys.goToVacancy) : trls(TrlKeys.openLink)}
                            </Button>
                        }
                    >
                        {vacancyId ? trls(TrlKeys.vacancyNameTooltip, [vacancyName]) : urlFromBanner}
                    </TooltipHover>
                    <TooltipHover delay={500} placement="top-center" activatorRef={copyIconActivatorRef}>
                        {vacancyId ? trls(TrlKeys.vacancyIdCopyIconTooltip) : trls(TrlKeys.linkCopyIconTooltip)}
                    </TooltipHover>
                </>
            )}
        </>
    );
};
